/* eslint-disable lit/binding-positions, lit/no-invalid-html */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { BlockStdScope } from '@blocksuite/block-std';
import { assertExists } from '@blocksuite/global/utils';
import { LitElement, nothing, } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { html, unsafeStatic } from 'lit/static-html.js';
import { RangeManager } from '../utils/range-manager.js';
import { WithDisposable } from '../with-disposable.js';
import { ShadowlessElement } from './shadowless-element.js';
let EditorHost = class EditorHost extends WithDisposable(ShadowlessElement) {
    constructor() {
        super(...arguments);
        this.blockIdAttr = 'data-block-id';
        this.widgetIdAttr = 'data-widget-id';
        this.rangeManager = null;
        this.renderModel = (model) => {
            const { flavour } = model;
            const schema = this.page.schema.flavourSchemaMap.get(flavour);
            if (!schema) {
                console.warn(`Cannot find schema for ${flavour}.`);
                return html `${nothing}`;
            }
            const view = this.std.spec.getView(flavour);
            if (!view) {
                console.warn(`Cannot find view for ${flavour}.`);
                return html `${nothing}`;
            }
            const tag = view.component;
            const widgets = view.widgets
                ? Object.entries(view.widgets).reduce((mapping, [key, tag]) => {
                    const template = html `<${tag} ${unsafeStatic(this.widgetIdAttr)}=${key} .host=${this} .page=${this.page}></${tag}>`;
                    return {
                        ...mapping,
                        [key]: template,
                    };
                }, {})
                : {};
            return html `<${tag}
      ${unsafeStatic(this.blockIdAttr)}=${model.id}
      .host=${this}
      .page=${this.page}
      .model=${model}
      .widgets=${widgets}
    ></${tag}>`;
        };
        this.renderModelChildren = (model) => {
            return html `${repeat(model.children, child => child.id, child => this.renderModel(child))}`;
        };
        this._registerView = () => {
            const blockSelector = `[${this.blockIdAttr}]`;
            const widgetSelector = `[${this.widgetIdAttr}]`;
            this.std.view.register({
                type: 'block',
                fromDOM: node => fromDOM(node, this.blockIdAttr, this.widgetIdAttr, 'block'),
                toDOM: ({ view }) => view,
                getChildren: node => getChildren(node, blockSelector, widgetSelector, 'block'),
            });
            this.std.view.register({
                type: 'widget',
                fromDOM: node => fromDOM(node, this.widgetIdAttr, this.blockIdAttr, 'widget'),
                toDOM: ({ view }) => view,
                getChildren: node => getChildren(node, blockSelector, widgetSelector, 'widget'),
            });
        };
    }
    get event() {
        return this.std.event;
    }
    get selection() {
        return this.std.selection;
    }
    get view() {
        return this.std.view;
    }
    get spec() {
        return this.std.spec;
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('specs')) {
            this.std.spec.applySpecs(this.specs);
        }
        super.willUpdate(changedProperties);
    }
    async getUpdateComplete() {
        const result = await super.getUpdateComplete();
        const root = this.page.root;
        assertExists(root);
        const view = this.std.spec.getView(root.flavour);
        assertExists(view);
        const widgetTags = Object.values(view.widgets ?? {});
        const elementsTags = [view.component, ...widgetTags];
        await Promise.all(elementsTags.map(tag => {
            const element = this.renderRoot.querySelector(tag._$litStatic$);
            if (element instanceof LitElement) {
                return element.updateComplete;
            }
            return;
        }));
        return result;
    }
    connectedCallback() {
        super.connectedCallback();
        if (!this.page.root) {
            throw new Error('This page is missing root block. Please initialize the default block structure before connecting the editor to DOM.');
        }
        this.std = new BlockStdScope({
            host: this,
            workspace: this.page.workspace,
            page: this.page,
        });
        this._registerView();
        this.std.mount();
        this.std.spec.applySpecs(this.specs);
        this.rangeManager = new RangeManager(this);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.std.unmount();
        this.rangeManager = null;
    }
    render() {
        const { root } = this.page;
        if (!root)
            return nothing;
        return this.renderModel(root);
    }
};
__decorate([
    property({ attribute: false })
], EditorHost.prototype, "specs", void 0);
__decorate([
    property({ attribute: false })
], EditorHost.prototype, "page", void 0);
__decorate([
    property({ attribute: false })
], EditorHost.prototype, "blockIdAttr", void 0);
__decorate([
    property({ attribute: false })
], EditorHost.prototype, "widgetIdAttr", void 0);
EditorHost = __decorate([
    customElement('editor-host')
], EditorHost);
export { EditorHost };
function fromDOM(node, target, notInside, type) {
    const selector = `[${target}]`;
    const notInSelector = `[${notInside}]`;
    const element = node && node instanceof HTMLElement ? node : node.parentElement;
    if (!element)
        return null;
    const view = element.closest(selector);
    if (!view) {
        return null;
    }
    const not = element.closest(notInSelector);
    if (view.contains(not)) {
        return null;
    }
    const id = view.getAttribute(target);
    assertExists(id);
    return {
        id,
        path: view.path,
        view,
        type,
    };
}
function getChildren(node, blockSelector, widgetSelector, type) {
    const selector = `${blockSelector},${widgetSelector}`;
    return Array.from(node.querySelectorAll(selector)).filter(x => x.parentElement?.closest(type === 'block' ? blockSelector : widgetSelector) === node);
}
//# sourceMappingURL=lit-host.js.map